/**
 * One opener per dialog-based analysis. `useLaunchAnalysis` dispatches here.
 * Re-runs with a past run's values (Compare surfaces) open through
 * `rerunOpeners.tsx` instead — kept separate so the station dock can import
 * them without pulling in every other dialog.
 *
 * The className/options each dialog is opened with live HERE and in
 * `rerunOpeners.tsx`; the `shellOpeners.test.ts` source scan holds every other
 * opener to the same.
 */
import type { ReactElement } from 'react';

import type { LayerData } from '@/atoms/layers.atom';
import { CrestLineDialog } from '@/components/CrestLineDialog';
import LandxmlExportDialog from '@/components/LandxmlExportDialog';
import LandxmlMergeDialog from '@/components/LandxmlMergeDialog';
import { RunCompareSurfacesDialog } from '@/components/RunCompareSurfacesDialog';
import { RunClearanceDialog } from '@/components/RunClearanceDialog';
import { RunGroundClassificationDialog } from '@/components/RunGroundClassificationDialog';
import { RunIprjExportDialog } from '@/components/RunIprjExportDialog';
import { RunMeshExportDialog } from '@/components/RunMeshExportDialog';
import { RunRainfallAnalysisDialog } from '@/components/RunRainfallAnalysisDialog';
import { ANALYSIS_DIALOG_OPTIONS, ANALYSIS_SHELL_CLASS, COMPARE_SHELL_CLASS } from '@/lib/analysisDialog';
import type { DialogOptions } from '@/providers/DialogProvider';

import { isClearanceCandidate, isCompareCandidate } from './candidates';
import type { ComparePatch } from './compare/formSchema';

export type OpenDialogFn = (content: ReactElement, className?: string, options?: DialogOptions) => void;

export interface OpenerEnv {
  openDialog: OpenDialogFn;
  projectId: string;
  layers: readonly LayerData[];
}

/** The crest-line dialog's class. `overflow-clip`, not `-hidden`: it clips the frame without
 *  making it a scroll container that a focus() or scrollIntoView() could scroll. */
export const CREST_LINE_DIALOG_CLASS = 'flex max-h-[80dvh] min-w-0 flex-col overflow-clip';

/** The one compare tool. The context layer seeds the dialog (it decides which
 *  slot from its type); `initialValues` carries a retry's form (Task 9's
 *  `useDeviationProgress` `onRetry`). */
export function openCompareSurfaces(
  env: OpenerEnv,
  contextLayer?: LayerData,
  initialValues?: ComparePatch,
): void {
  env.openDialog(
    <RunCompareSurfacesDialog
      projectId={env.projectId}
      candidateLayers={env.layers.filter(isCompareCandidate)}
      contextLayer={contextLayer}
      initialValues={initialValues}
    />,
    COMPARE_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}

export function openClearance(env: OpenerEnv, source: LayerData): void {
  env.openDialog(
    <RunClearanceDialog
      projectId={env.projectId}
      sourceLayer={source}
      candidateLayers={env.layers.filter(isClearanceCandidate)}
    />,
    ANALYSIS_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}

export function openGroundClassify(env: OpenerEnv, source?: LayerData): void {
  env.openDialog(
    <RunGroundClassificationDialog sourceLayer={source} projectId={env.projectId} />,
    ANALYSIS_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}

export function openMesh(env: OpenerEnv, source?: LayerData): void {
  env.openDialog(
    <RunMeshExportDialog sourceLayer={source} projectId={env.projectId} />,
    ANALYSIS_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}

export function openIprjExport(env: OpenerEnv, source?: LayerData): void {
  env.openDialog(
    <RunIprjExportDialog sourceLayer={source} projectId={env.projectId} />,
    ANALYSIS_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}

export function openRainfall(env: OpenerEnv, source?: LayerData): void {
  env.openDialog(
    <RunRainfallAnalysisDialog sourceLayer={source} projectId={env.projectId} />,
    ANALYSIS_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}

/** Non-modal on purpose — see `DialogOptions.modal`. Never moves onto the shell (plan D-P3-2). */
export function openCrestLine(env: OpenerEnv, sourceLayerId: string, srid: number): void {
  env.openDialog(
    <CrestLineDialog sourceLayerId={sourceLayerId} projectId={env.projectId} srid={srid} />,
    CREST_LINE_DIALOG_CLASS,
    { modal: false },
  );
}

export function openLandxmlExport(env: OpenerEnv, layer: LayerData): void {
  env.openDialog(
    <LandxmlExportDialog projectId={env.projectId} layerId={layer.id} layerName={layer.name} />,
    ANALYSIS_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}

export function openLandxmlMerge(env: OpenerEnv, layer?: LayerData, initial?: {
  sourceLayerIds?: string[];
  outputName?: string;
  missingInputs?: boolean;
}): void {
  env.openDialog(
    <LandxmlMergeDialog projectId={env.projectId} initialLayerId={layer?.id}
      initialSourceLayerIds={initial?.sourceLayerIds} initialName={initial?.outputName}
      missingInputs={initial?.missingInputs} />,
    ANALYSIS_SHELL_CLASS,
    ANALYSIS_DIALOG_OPTIONS,
  );
}
